import Button from '@/components/Buttons/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Filter from '@/images/Filter.svg';
import { capitalizeFirstLetter } from '@/pages/agents/utils';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Select } from '@mui/material';
import { useState } from 'react';

const StyledSelect = styled(Select)`
    padding: 0;
    color: ${theme.colors.secondary[700]};
    cursor: pointer;

    fieldset {
        border: none;
    }

    .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input {
        padding: 0;
    }

    .MuiSvgIcon-root {
        color: ${theme.colors.secondary[900]};
    }
` as unknown as typeof Select;

const ActionsFooter = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 12px;
    border-top: 1px solid ${theme.colors.black[300]};
    position: sticky;
    bottom: 0;
    width: 100%;
`;

const StyledButton = styled(Button)`
    padding: 0 12px;
    border-radius: 6px;
    height: 32px;
    font-size: 14px;
`;

const ListElement = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    outline: none;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    user-select: none;
`;

const OptionsList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: calc(100% - 45px);
    overflow-y: auto;
    outline: none;
    padding-bottom: 8px;
`;

const StyledFilterButton = styled(Button)<{ active: boolean }>`
    padding: 8px 16px;
    border-radius: 8px;
    gap: 8px;
    font-weight: 400;
    color: ${theme.colors.secondary.main};
    border: 1px solid ${({ active }) => (active ? theme.colors.secondary.main : theme.colors.secondary[300])};
`;

interface Props {
    options: string[];
    value: string[];
    onSubmit: (value: string[]) => void;
    label: string;
}

export const TableFilter = ({ options, value, onSubmit, label }: Props) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>(value);

    const handleApply = () => {
        onSubmit(selected);
        handleClose();
    };

    const handleClear = () => {
        setSelected([]);
        onSubmit([]);
        handleClose();
    };

    const handleSelect = (value: string) => {
        setSelected(prev => {
            if (prev.includes(value)) {
                return prev.filter(v => v !== value);
            }
            return [...prev, value];
        });
    };

    const handleOpen = () => {
        setSelected(value);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const FilterButton = () => (
        <StyledFilterButton variant="outlined" color="secondary" active={open}>
            <MaskedIcon src={Filter} size="16px" />
            {value?.length ? `${label} • ${value.length}` : label}
        </StyledFilterButton>
    );

    return (
        <StyledSelect
            multiple
            value={selected}
            renderValue={FilterButton}
            displayEmpty
            IconComponent={null as any}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '8px !important',
                        padding: '12px',
                        minWidth: '290px !important',
                        maxWidth: '290px !important',
                        height: '390px !important',
                        boxShadow:
                            '0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12), 1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17), 2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23), 5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29)',
                    },
                },
                MenuListProps: {
                    sx: { padding: '0', height: '100%', overflow: 'hidden' },
                },
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                },
                transformOrigin: {
                    vertical: -4,
                    horizontal: 'right',
                },
            }}
            open={open}
            onClose={handleClose}
            onOpen={handleOpen}>
            <OptionsList>
                {options.map(option => (
                    <ListElement key={option} onClick={() => handleSelect(option)}>
                        <Checkbox checked={selected.includes(option)} size="16px" />
                        {capitalizeFirstLetter(option)}
                    </ListElement>
                ))}
            </OptionsList>
            <ActionsFooter>
                <StyledButton onClick={handleClear} variant="text" color="secondary">
                    Clear
                </StyledButton>
                <StyledButton onClick={handleApply} variant="contained" color="secondary">
                    Apply
                </StyledButton>
            </ActionsFooter>
        </StyledSelect>
    );
};
